import { useMemo, useEffect, useState } from "react";

import { chosenSites } from "app/common/services/chosenSites";
import { CHART_COLORS } from "app/constants/ChartColors";
import { allTrackers } from "app/services/track/track";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { useCommonVisitsOvertime } from "../hooks/useCommonVisitsOvertime";
import { useVisitsOvertimeData } from "../hooks/useVisitsOvertimeData";
import { createChartData, createLegendItem } from "../utils";

import { processCompareModeData } from "./utils";

export const useVisitsOvertimeCompareMode = () => {
    const { data, isLoading, excelUrl } = useVisitsOvertimeData();
    const { legendItems, setLegendItems, colorStack, config } = useCommonVisitsOvertime();

    const initialLob = (proNavigator.getParams()?.graphCategory as string) || "";
    const [selectedLob, setSelectedLob] = useState<string>(initialLob);

    const { processedData, availableLobs, lobVisitsData } = useMemo(
        () => processCompareModeData(data, selectedLob),
        [data, selectedLob],
    );

    const isValidLob = selectedLob && availableLobs.includes(selectedLob);
    const shouldAutoSelect = availableLobs.length > 0 && (!selectedLob || !isValidLob);

    if (shouldAutoSelect) {
        const newSelectedLob = availableLobs[0];
        setSelectedLob(newSelectedLob);
        proNavigator.applyUpdateParams({ graphCategory: newSelectedLob }, { reload: false });
    }

    const createLegendItems = useMemo(() => {
        if (!processedData) {
            return [];
        }

        const origColors = CHART_COLORS["compareMainColors"];
        const domainItems = [];

        chosenSites.forEach((site, siteInfo, index) => {
            if (processedData.total[site]) {
                const monthlyVisits = Object.values(processedData.breakdown)
                    .map((monthData) => monthData[site]?.Visits || 0)
                    .filter((visits) => visits > 0);

                const monthlyAverage =
                    monthlyVisits.length > 0
                        ? monthlyVisits.reduce((sum, visits) => sum + visits, 0) / monthlyVisits.length
                        : 0;

                domainItems.push(
                    createLegendItem({
                        name: site,
                        visible: true,
                        visits: monthlyAverage,
                        color: origColors[index], // Use the same color logic as GroupTrafficShare
                    }),
                );
            }
        });

        const totalMonthlyVisits = Object.values(processedData.breakdown)
            .map((monthData) => Object.values(monthData).reduce((sum, siteData) => sum + (siteData?.Visits || 0), 0))
            .filter((visits) => visits > 0);

        const totalMonthlyAverage =
            totalMonthlyVisits.length > 0
                ? totalMonthlyVisits.reduce((sum, visits) => sum + visits, 0) / totalMonthlyVisits.length
                : 0;

        const totalItem =
            totalMonthlyAverage > 0
                ? createLegendItem({
                      name: selectedLob,
                      visible: false,
                      visits: totalMonthlyAverage,
                      isTotal: true,
                      useThreshold: false,
                  })
                : null;

        return totalItem ? [...domainItems, totalItem] : domainItems;
    }, [processedData, selectedLob]);

    useEffect(() => {
        setLegendItems(createLegendItems);
    }, [createLegendItems, setLegendItems]);

    const chartData = createChartData(processedData, legendItems);
    const visibleChartData = chartData.filter((series) => series.visible);

    const handleSelectedCategory = (id: string) => {
        setSelectedLob(id);
        allTrackers.dispatchTrackEvent("dropdown", "select", "over time", { id });
        proNavigator.applyUpdateParams({ graphCategory: id }, { reload: false });
    };
    return {
        selectedLob,
        handleSelectedCategory,
        isLoading,
        config,
        legendItems,
        chartData,
        visibleChartData,
        setLegendItems,
        availableLobs,
        lobVisitsData,
        excelUrl,
    };
};
